import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import {
  getDashboardMetric4,
  type DashboardMetric4CycleKeyControlCount,
} from '@/services/dashboardService';

const { Text } = Typography;

const KEY_COLOR = '#3B82F6'; // blue
const NONKEY_COLOR = '#F59E0B'; // orange

type Datum = {
  cycleId: string;
  cycleCode: string;
  cycleName: string;
  cycleLabel: string; // "A05 ITGC"
  key: number;
  nonkey: number;
};

export type EX1ControlDistributionKeyVsNonKeyByCycleGroupedHorizontalBarChartProps =
  {
    height?: number;
    title?: string;
  };

const Metric4Tooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  const keyItem = payload.find((p: any) => p.dataKey === 'key');
  const nonKeyItem = payload.find((p: any) => p.dataKey === 'nonkey');

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 220,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>

      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
      >
        <span>Key</span>
        <span style={{ fontWeight: 700 }}>{keyItem?.value ?? 0}</span>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          marginTop: 4,
        }}
      >
        <span>Non-Key</span>
        <span style={{ fontWeight: 700 }}>{nonKeyItem?.value ?? 0}</span>
      </div>

      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click a bar to open Exhibit 5 filtered by Cycle + Key/Non-Key
      </div>
    </div>
  );
};

export const EX1ControlDistributionKeyVsNonKeyByCycleGroupedHorizontalBarChart: React.FC<
  EX1ControlDistributionKeyVsNonKeyByCycleGroupedHorizontalBarChartProps
> = ({
  height = 380,
  title = 'Control Distribution Key vs Non-Key by Cycle',
}) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric4CycleKeyControlCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric4(annualReportId);
        if (!isMounted) return;
        setRaw(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw([]);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 4 (Key vs Non-Key controls by cycle).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const data: Datum[] = useMemo(() => {
    return (
      raw
        .map(c => ({
          cycleId: c.cycleId,
          cycleCode: c.cycleCode,
          cycleName: c.cycleName,
          cycleLabel: `${c.cycleCode} ${c.cycleName}`.trim(),
          key: c.keyControlCount ?? 0,
          nonkey: c.nonkeyControlCount ?? 0,
        }))
        // match example feel: sort by (key+nonkey) desc
        //.sort((a, b) => b.key + b.nonkey - (a.key + a.nonkey))
    );
  }, [raw]);

  const totals = useMemo(() => {
    return data.reduce(
      (acc, d) => {
        acc.key += d.key ?? 0;
        acc.nonkey += d.nonkey ?? 0;
        return acc;
      },
      { key: 0, nonkey: 0 },
    );
  }, [data]);

  const maxTotal = useMemo(() => {
    // axis needs to cover the largest (key + nonkey) in any row
    return Math.max(0, ...data.map(d => d.key ?? 0), ...data.map(d => d.nonkey ?? 0));
  }, [data]);

  const xTicks = useMemo(() => {
    // same pattern as your other chart: 1..maxTotal
    return Array.from({ length: maxTotal }, (_, i) => i + 1);
  }, [maxTotal]);

  const computedTitle =
    !loading && !errorMessage && data.length > 0
      ? `Control Distribution Key (Total Count: ${totals.key}) and Non-Key (Total Count: ${totals.nonkey}) by Cycle`
      : title;

  const isEmpty = !loading && !errorMessage && data.length === 0;

  // Adjust query keys to match Exhibit 5 filtering
  const goToExhibit5 = (cycleId: string, cycleCode: string, cycleName: string, keyType: 'key' | 'nonkey') => {
    navigate('/control-activities', {
      state: {
        cycleId: cycleId,
        cycleCode: cycleCode,
        cycleName: cycleName,
        isKey: keyType == 'key' ? true : false,
      },
    });
  };

  const computedHeight = useMemo(() => {
    const rowHeight = 55; // tweak 24–36 based on your font/spacing
    const min = height;
    const needed = data.length * rowHeight + 80; // padding for axes/legend
    return Math.max(min, needed);
  }, [data.length, height]);

  return (
    <Card title={computedTitle} extra={<Text type='secondary'></Text>}>
      <div style={{ height: computedHeight }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 4 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No cycles were returned.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={data}
              layout='vertical'
              margin={{ top: 8, right: 80, bottom: 24, left: 32 }}
              barCategoryGap={14}
              barGap={6}
            >
              <XAxis
                type='number'
                domain={[0, maxTotal]}
                ticks={xTicks}
                allowDecimals={false}
                label={{
                  value: 'Count of Controls',
                  position: 'insideBottom',
                  offset: -10,
                }}
              />
              <YAxis
                type='category'
                dataKey='cycleLabel'
                width={190}
                interval={0}
                tickMargin={8}
                label={{
                  value: 'Cycle Name',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -25,
                }}
              />

              <Tooltip content={<Metric4Tooltip />} />
              <Legend
                layout='vertical'
                align='right'
                verticalAlign='middle'
                wrapperStyle={{ paddingLeft: 26 }}
                formatter={(value: string) => {
                  switch (value) {
                    case 'Key':
                      return `Key (${totals.key})`;
                    case 'Non-Key':
                      return `Non-Key (${totals.nonkey})`;
                    default:
                      return value;
                  }
                }}
              />

              <Bar
                dataKey='key'
                name='Key'
                fill={KEY_COLOR}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const cycleId = barData?.payload?.cycleId;
                  const cycleCode = barData?.payload?.cycleCode;
                  const cycleName = barData?.payload?.cycleName;
                  if (cycleId) goToExhibit5(cycleId, cycleCode, cycleName, 'key');
                }}
                style={{ cursor: 'pointer' }}
              >
                {/* <LabelList position='right' formatter={(v: any) => `${v}`} /> */}
              </Bar>

              <Bar
                dataKey='nonkey'
                name='Non-Key'
                fill={NONKEY_COLOR}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const cycleId = barData?.payload?.cycleId;
                  const cycleCode = barData?.payload?.cycleCode;
                  const cycleName = barData?.payload?.cycleName;
                  if (cycleId) goToExhibit5(cycleId, cycleCode, cycleName, 'nonkey');
                }}
                style={{ cursor: 'pointer' }}
              >
                {/* <LabelList position='right' formatter={(v: any) => `${v}`} /> */}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default EX1ControlDistributionKeyVsNonKeyByCycleGroupedHorizontalBarChart;
